/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 * Widget Personal Account component
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Misha Kokoulin
 * @author Maxim Fedorov
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Julia Ignatieva
 * @author Alexander Golshtein
 * @version 1.25.0
 * @since 1.0.1
 */
import { Fragment, memo, useCallback, useEffect } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { LinkBlock, Skeleton } from '../../../../../../components/common';
import { FlexColumnStyled } from '../../../../../../components/common/Block/Flexbox';
import { LayoutStyled } from '../../../../../../components/common/Block/Layout';
import { openMobileCash } from '../../../../../../components/Table/actions/tableActions';
import { IOpenMobileCashPayload } from '../../../../../../components/Table/types/tableTypes';
import { pushGTMAnalytic } from '../../../../../../modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { IGTMPushAnalyticPayload } from '../../../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import initTranslationCommon from '../../../../../../modules/internationalization/constants/common';
import initTranslationPageDashboard
    from '../../../../../../modules/internationalization/constants/pages/dashboard/dashboard';
import { makeSelectPageAccountInfoTranslation } from '../../../../../../modules/internationalization/intSelectors';
import ICommonTranslation from '../../../../../../modules/internationalization/types/common';
import IPageDashboardTranslation from '../../../../../../modules/internationalization/types/pages/dashboard/dashboard';
import { IPageAccountInfoTranslation } from '../../../../../../modules/internationalization/types/pages/pageAccountInfoTranslation';
import { logBusinessEvent } from '../../../../../../modules/logging/loggingActions';
import { ELogBusinessEventName, ELogBusinessEventType } from '../../../../../../modules/logging/loggingConstants';
import { ILogBusinessEventPayload } from '../../../../../../modules/logging/loggingTypes';
import { modalOpen } from '../../../../../../modules/modal/modalActions';
import { IModalOpenPayload } from '../../../../../../modules/modal/types/modalActionTypes';
import { AccessComponentKeyEnum, FeatureNameEnum } from '../../../../../../modules/role/constants/guard/guardConstants';
import {
    makeSelectGuardComponentEnabled,
} from '../../../../../../modules/role/selectors/guardSelector';
import { makeSelectLocalStorageSelectedAccount } from '../../../../../../modules/storage/storageSelectors';
import { IRootReducer } from '../../../../../../reducers/rootReducer';
import { CLIENT_ID, RoutePath } from '../../../../../../routes/constants/routesConstants';
import { EAccountType } from '../../../../../PageAuth/constants/authConstants';
import { makeSelectIsFeatureWithVersionEnabled } from '../../../../../PageAuth/selectors/authSelectors';
import {
    WidgetSkeletonButton,
    WidgetSkeletonParagraph,
    WidgetSkeletonParagraphNormal,
} from '../../constants/widgetSkeletonContants';
import { WidgetSubtitleStyled, WidgetTitleStyled } from '../../styled/WidgetStyled';
import WidgetError from '../../WidgetError';
import { IWidgetSearchAccountsItem } from '../../WidgetSearchAccounts/types/widgetSearchAccountsTypes';
import { EWidgetCommonStatus } from '../../widgetTypes';
import {
    WidgetPersonalAccountContainerStyled,
    WidgetPersonalAccountLinkStyled,
    WidgetPersonalAccountTypeStyled,
} from '../styled/WidgetPersonalAccountStyled';
import {
    IWidgetPersonalAccountCashbackUrl,
    IWidgetPersonalAccountSuccessProps,
} from '../types/widgetPersonalAccountType';
import { widgetPersonalAccountLoadData } from '../widgetPersonalAccountActions';
import {
    makeSelectorWidgetPersonalAccountStatus,
    makeSelectWidgetPersonalAccountBalanceDate,
    makeSelectWidgetPersonalAccountCashbackAmount,
    makeSelectWidgetPersonalAccountCashbackUrl,
    makeSelectWidgetPersonalAccountCreditBalance,
    makeSelectWidgetPersonalAccountCurrentBalance,
    makeSelectWidgetPersonalAccountHasNewBills,
    makeSelectWidgetPersonalAccountIsLoading,
    makeSelectWidgetPersonalAccountNearestPayDate,
    makeSelectWidgetPersonalAccountNumber,
    makeSelectWidgetPersonalAccountOutflowType,
    makeSelectWidgetPersonalAccountPromisedPaymentAmount,
    makeSelectWidgetPersonalAccountPromisedPaymentButton,
    makeSelectWidgetPersonalAccountPromisedPaymentEndDate,
    makeSelectWidgetPersonalAccountTotalBalance,
    makeSelectWidgetPersonalAccountUnpaidBillsAmount,
    makeSelectWidgetPersonalAccountUnpaidBillsQuantity,
} from '../widgetPersosnalAccountSelector';
import WidgetPersonalAccountSuccess from './WidgetPersonalAccountSuccess';

interface IConnectedProps {
    accountNumber: string;
    activePromisedPaymentAmount?: number;
    activePromisedPaymentEndDate?: string;
    balanceDate: string;
    cashbackAmount: number;
    cashbackUrl: IWidgetPersonalAccountCashbackUrl;
    creditLimit?: number;
    currentBalance: number;
    isAccountInfoGeneralEnabled: boolean;
    isCashbackV2Enabled?: boolean;
    isLoading: boolean;
    nearestPayDate?: string;
    outflowType: EAccountType;
    promisedPaymentButtonAvailable?: boolean;
    selectedAccount: IWidgetSearchAccountsItem;
    status: EWidgetCommonStatus;
    totalBalance?: number;
    translationAccountInfo: IPageAccountInfoTranslation;
    unpaidBillsAmount?: number;
    unpaidBillsQuantity?: number;
    hasNewBills?: boolean;
}

interface IDispatchProps {
    loadData: () => void;
    logBusinessEvent: (payload: ILogBusinessEventPayload) => void;
    openModal: (payload: IModalOpenPayload) => void;
    openMobileCash: (payload: IOpenMobileCashPayload) => void;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
}

interface IWidgetPersonalAccountProps {
    translation: IPageDashboardTranslation & ICommonTranslation;
}

export type TWidgetPersonalAccount = IConnectedProps & IDispatchProps & IWidgetPersonalAccountProps;

export const WidgetPersonalAccount = (props: TWidgetPersonalAccount) => {

    const { loadData, isLoading, status, isAccountInfoGeneralEnabled, ...rest } = props;

    const translation = props.translation.widgets.personalAccount;
    const balancesTranslation = props.translationAccountInfo.financialInfo.balances;

    useEffect(() => {
        loadData();
    }, [loadData, props.selectedAccount.id]);

    const convertLoadData = (): Omit<IWidgetPersonalAccountSuccessProps, 'accountId'> => {
        const {
            cashbackAmount,
            creditLimit,
            currentBalance,
            totalBalance,
            translation,
            ...other
        } = rest;

        const result = {
            creditLimit: {
                value: creditLimit,
                description: balancesTranslation.creditLimit.hint,
            },
            currency: translation.currency,
            currentBalance: {
                value: currentBalance,
                description: balancesTranslation.currentBalance.hint,
            },
            scores: translation.scores,
            totalBalance: {
                value: totalBalance,
                description: balancesTranslation.totalBalance.hint,
            },
            translation: translation.widgets.personalAccount.success,
            ...other,
        };

        if (cashbackAmount !== null) {
            // @ts-ignore
            result.cashBack = {
                value: cashbackAmount,
                description: translation.widgets.personalAccount.success.cashBackDescription,
            };
        }

        return result;
    };

    const onClickPersonalAccountLink = () => {
        props.logBusinessEvent({
            eventName: ELogBusinessEventName.USER_ACCOUNT,
            type: ELogBusinessEventType.common,
        });
        // GTM Analytic
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetPersonalAccount.clickLinkWidgetPersonalAccount,
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    };

    const getAccountInfoUrl = useCallback(() => {
        if (isAccountInfoGeneralEnabled) {
            return RoutePath.accountInfoGeneral.replace(CLIENT_ID, props.selectedAccount.id.toString());
        }
        return RoutePath.accountInfoExternal.replace(CLIENT_ID, props.selectedAccount.id.toString());
    }, [isAccountInfoGeneralEnabled, props.selectedAccount]);

    return (
        <FlexColumnStyled>
            <WidgetPersonalAccountContainerStyled
                data-widget="WidgetPersonalAccount"
                isLoading={isLoading}>

                <LayoutStyled className="mb-4">
                    <WidgetTitleStyled>
                        {translation.title}
                    </WidgetTitleStyled>

                    <WidgetSubtitleStyled>
                        <LinkBlock
                            dataName="redirectToAccountInfo"
                        >
                            <WidgetPersonalAccountLinkStyled
                                href={getAccountInfoUrl()}
                                onClick={onClickPersonalAccountLink}
                            >
                                {rest.accountNumber}
                            </WidgetPersonalAccountLinkStyled>
                        </LinkBlock>
                        <Skeleton
                            isLoading={isLoading}
                            render={() => WidgetSkeletonParagraphNormal}
                        >
                            <WidgetPersonalAccountTypeStyled>
                                {status === EWidgetCommonStatus.SUCCESS &&
                                rest.outflowType &&
                                rest.outflowType !== EAccountType.EMPTY ?
                                    `, ${rest.translation.widgets.personalAccount.outflowType[rest.outflowType]}` : ''}
                            </WidgetPersonalAccountTypeStyled>
                        </Skeleton>
                    </WidgetSubtitleStyled>
                </LayoutStyled>

                <Skeleton
                    count={3}
                    isLoading={isLoading}
                    render={() => WidgetSkeletonParagraph}
                >
                    <Fragment>
                        {{
                            error: <WidgetError text={translation.error}/>,
                            success: <WidgetPersonalAccountSuccess
                                {...convertLoadData()}
                                accountId={props.selectedAccount.id}
                                cashbackUrl={props.cashbackUrl}
                                openMobileCash={props.openMobileCash}
                            />,
                        }[status]}
                    </Fragment>
                </Skeleton>

                <Skeleton
                    isLoading={isLoading}
                    render={() => WidgetSkeletonButton}
                >
                    <Fragment/>
                </Skeleton>
            </WidgetPersonalAccountContainerStyled>
        </FlexColumnStyled>
    );
};

WidgetPersonalAccount.defaultProps = {
    translation: { ...initTranslationPageDashboard, ...initTranslationCommon },
};

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    accountNumber: makeSelectWidgetPersonalAccountNumber(),
    activePromisedPaymentAmount: makeSelectWidgetPersonalAccountPromisedPaymentAmount(),
    activePromisedPaymentEndDate: makeSelectWidgetPersonalAccountPromisedPaymentEndDate(),
    balanceDate: makeSelectWidgetPersonalAccountBalanceDate(),
    cashbackAmount: makeSelectWidgetPersonalAccountCashbackAmount(),
    cashbackUrl: makeSelectWidgetPersonalAccountCashbackUrl(),
    creditLimit: makeSelectWidgetPersonalAccountCreditBalance(),
    currentBalance: makeSelectWidgetPersonalAccountCurrentBalance(),
    isCashbackV2Enabled: makeSelectIsFeatureWithVersionEnabled(FeatureNameEnum.CASHBACK, [2, 3, 4]),
    isAccountInfoGeneralEnabled: makeSelectGuardComponentEnabled(AccessComponentKeyEnum.PageAccountInfoGeneral),
    isLoading: makeSelectWidgetPersonalAccountIsLoading(),
    nearestPayDate: makeSelectWidgetPersonalAccountNearestPayDate(),
    outflowType: makeSelectWidgetPersonalAccountOutflowType(),
    promisedPaymentButtonAvailable: makeSelectWidgetPersonalAccountPromisedPaymentButton(),
    selectedAccount: makeSelectLocalStorageSelectedAccount(),
    status: makeSelectorWidgetPersonalAccountStatus(),
    totalBalance: makeSelectWidgetPersonalAccountTotalBalance(),
    translationAccountInfo: makeSelectPageAccountInfoTranslation(),
    unpaidBillsAmount: makeSelectWidgetPersonalAccountUnpaidBillsAmount(),
    unpaidBillsQuantity: makeSelectWidgetPersonalAccountUnpaidBillsQuantity(),
    hasNewBills: makeSelectWidgetPersonalAccountHasNewBills(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    loadData: () => dispatch(widgetPersonalAccountLoadData()),
    logBusinessEvent: (payload) => dispatch(logBusinessEvent(payload)),
    openMobileCash: (payload: IOpenMobileCashPayload) => dispatch(openMobileCash(payload)),
    openModal: (payload: IModalOpenPayload) => dispatch(modalOpen(payload)),
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => dispatch(pushGTMAnalytic(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(WidgetPersonalAccount));
